import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import type { NmoNavigatedAction } from './router-state-serializer';
import { UrlUtilsService } from '@candyland/angular/shared/util-nmo';
import type { GroupedObservable, Observable } from 'rxjs';
import { filter, groupBy, mergeAll, share } from 'rxjs/operators';

export const PAGE_VIEW_CASES: InjectionToken<PageViewCase<string>[]> = new InjectionToken<PageViewCase<string>[]>(
  'PageView Types with predicates to find first match'
);

@Injectable({ providedIn: 'root' })
export class PageViewsService<T extends string> {
  private pageViewCases: PageViewCase<T | 'all-remaining'>[];
  private pageViewGroups$: Observable<GroupedObservable<T | 'all-remaining' | 'no-op', NmoNavigatedAction>> =
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      groupBy(
        (r: NmoNavigatedAction) =>
          this.pageViewCases.find((potentialMatch: PageViewCase<T | 'all-remaining'>) => potentialMatch.predicate(r))
            .pageViewType
      ),
      share()
    );

  constructor(
    private actions$: Actions,
    private urlUtils: UrlUtilsService,
    @Inject(PAGE_VIEW_CASES) pageViewCases: PageViewCase<T>[]
  ) {
    this.pageViewCases = [...pageViewCases, { predicate: (): boolean => true, pageViewType: 'all-remaining' }];
  }

  getFiltered(types: (T | 'all-remaining')[]): Observable<NmoNavigatedAction> {
    return this.pageViewGroups$.pipe(
      filter(
        (pageViews$: GroupedObservable<T | 'all-remaining' | 'no-op', NmoNavigatedAction>): boolean =>
          types.indexOf(pageViews$.key as any) !== -1
      ),
      mergeAll()
    );
  }

  getAll(): Observable<NmoNavigatedAction> {
    return this.pageViewGroups$.pipe(mergeAll());
  }

  pathHasNotChanged(a: NmoNavigatedAction, b: NmoNavigatedAction): boolean {
    const haveSamePath: boolean = this.urlUtils.haveSamePath(a.payload.routerState.url, b.payload.routerState.url);
    const areConsecutiveNavigations: boolean = b.payload.event.id - b.payload.event.id === 1;
    return haveSamePath && areConsecutiveNavigations;
  }
}

interface PageViewCase<T extends string> {
  predicate: (r: NmoNavigatedAction) => boolean;
  pageViewType: T | 'no-op';
}
